import { useRef } from 'react';

import { AnimatedCounter, NavyAtmosphere, PointerParallax, ScrollReveal } from '@/components/motion';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP } from '@/lib/gsap';
import { cn } from '@ibas/ui';

interface ProofFigure {
  id: string;
  value: number;
  decimals?: number;
  suffix?: string;
  /** Caption always carries year/scope context (Claims Rule). */
  caption: string;
  /** Pointer-parallax travel multiplier — figures drift at slightly different depths. */
  depth: number;
}

const FIGURES: ProofFigure[] = [
  {
    id: 'graduates',
    value: 3500,
    suffix: '+',
    caption: 'Graduates across all programmes',
    depth: 0.6,
  },
  {
    id: 'nationalities',
    value: 63,
    suffix: '+',
    caption: 'Student nationalities',
    depth: 0.85,
  },
  { id: 'centres', value: 8, suffix: '+', caption: 'Global learning centres', depth: 1.1 },
  {
    id: 'continents',
    value: 6,
    suffix: '+',
    caption: 'Continents represented by our student body',
    depth: 1.25,
  },
  { id: 'faculty', value: 25, suffix: '+', caption: 'Faculty members', depth: 1.4 },
];

/**
 * Per-item divider classes for the five-figure grid, indexed to each figure's
 * fixed position (1-5). The grid reflows at grid-cols-2 (base) ->
 * grid-cols-3 (sm) -> grid-cols-5 (lg); rather than a modulo/nth-child
 * pattern, each position's row/column membership at every breakpoint is
 * resolved explicitly so the top-border (row) and left-border (column)
 * dividers stay correct even when a breakpoint's last row is partial
 * (e.g. the fifth figure sitting alone in the final row at 2- and 3-up).
 */
const DIVIDER_CLASSES: string[] = [
  // pos 1: row 1 / col 1 at every breakpoint
  'border-t-0 border-l-0 pt-0 pl-0',
  // pos 2: row 1 / col 2 at every breakpoint
  'border-t-0 pt-0 border-l pl-6 lg:pl-8',
  // pos 3: row 2 (base) -> row 1 (sm+) / col 1 (base) -> col 3 (sm+)
  'border-t pt-6 border-l-0 pl-0 sm:border-t-0 sm:pt-0 sm:border-l sm:pl-6 lg:pl-8',
  // pos 4: row 2 (base, sm) -> row 1 (lg) / col 2 (base) -> col 1 (sm) -> col 4 (lg)
  'border-t pt-6 border-l pl-6 sm:border-l-0 sm:pl-0 lg:border-t-0 lg:pt-0 lg:border-l lg:pl-8',
  // pos 5: row 3 (base) -> row 2 (sm) -> row 1 (lg) / col 1 (base) -> col 2 (sm) -> col 5 (lg)
  'border-t pt-6 border-l-0 pl-0 sm:border-l sm:pl-6 lg:border-t-0 lg:pt-0 lg:pl-8',
];

export interface ProofStatsProps {
  className?: string;
}

/**
 * Proof band pairing the house AnimatedCounter with a ScrollReveal-staggered
 * grid — five institutional figures count up as the band enters view, each
 * counter grounded by a caption carrying its year/scope per the Claims Rule.
 *
 * Motion intent: a quiet, confident "here is the evidence" beat after a claim
 * -heavy narrative section — figures arrive one after another like a case
 * being made, not a burst of numbers. A muted campus video plays low behind
 * a heavy navy overlay so the band feels alive without competing with the
 * numbers; a gold hairline draws in as the section enters view, and the
 * whole figure row drifts in subtle layers with the cursor for a premium,
 * tactile feel. Under reduced motion every counter renders its final value
 * immediately (handled inside AnimatedCounter), the video is replaced by its
 * static poster, the divider is fully drawn from the start, and the pointer
 * drift is disabled — the section reads as a plain, static grid.
 */
export function ProofStats({ className }: ProofStatsProps) {
  const rootRef = useRef<HTMLElement>(null);
  const dividerRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      const divider = dividerRef.current;
      if (!divider) return;

      if (reduced) {
        gsap.set(divider, { scaleX: 1 });
        return;
      }

      gsap.set(divider, { scaleX: 0, transformOrigin: 'left center' });
      gsap.to(divider, {
        scaleX: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: rootRef.current,
          start: 'top 75%',
          end: 'top 30%',
          scrub: 0.6,
        },
      });
    },
    { scope: rootRef, dependencies: [reduced] },
  );

  return (
    <section
      ref={rootRef}
      aria-labelledby="proof-stats-heading"
      className={cn(
        'relative isolate overflow-hidden bg-brand-navy px-6 py-16 sm:py-20 lg:px-16',
        className,
      )}
    >
      {/* Static navy atmosphere instead of footage — the collaboration video
          stays unique to the hero, so this proof band doesn't duplicate it.
          Shares the WordHero / closing-CTA decorative motif for cohesion. */}
      <NavyAtmosphere />

      <div className="relative z-10 mx-auto max-w-6xl">
        <div className="text-center">
          <p className="font-label text-xs tracking-[0.35em] text-brand-gold uppercase sm:text-sm">
            Proof, not promises
          </p>
          <h2 id="proof-stats-heading" className="sr-only">
            IBAS by the numbers
          </h2>
          <div
            ref={dividerRef}
            aria-hidden="true"
            className="mx-auto mt-6 h-px w-24 bg-brand-gold"
          />
        </div>

        <PointerParallax className="mt-10 sm:mt-12">
          <ScrollReveal
            stagger={0.12}
            className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 lg:grid-cols-5 lg:gap-x-8"
          >
            {FIGURES.map((figure, index) => (
              <div
                key={figure.id}
                data-parallax
                data-depth={figure.depth}
                className={cn('border-white/15 text-center', DIVIDER_CLASSES[index])}
              >
                <AnimatedCounter
                  value={figure.value}
                  decimals={figure.decimals}
                  suffix={figure.suffix}
                  className="block font-display text-4xl font-medium text-brand-gold sm:text-5xl lg:text-6xl"
                />
                <p className="mt-3 font-label text-[0.7rem] tracking-[0.22em] text-white/70 uppercase sm:text-xs">
                  {figure.caption}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </PointerParallax>
      </div>
    </section>
  );
}
